import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Target, Flame, Check, Trash2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const suggestedHabits = [
  { name: 'Morning Hydration', description: 'Drink a glass of water within 30 minutes of waking' },
  { name: 'Evening Stretch', description: 'Complete 10 minutes of stretching before bed' },
  { name: 'Mindful Breathing', description: 'Practice 5 minutes of deep breathing daily' },
  { name: 'Daily Walk', description: 'Take a 15-minute walk after lunch' },
  { name: 'Screen-Free Hour', description: 'No screens 1 hour before bed' },
  { name: 'Gratitude Journal', description: 'Write 3 things you are grateful for' },
  { name: 'Veggie with Every Meal', description: 'Add a serving of vegetables to each meal' },
  { name: 'Stand & Stretch Hourly', description: 'Stand up and stretch every hour during work' },
];

export default function HabitsPage() {
  const [activeHabits, setActiveHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newHabitName, setNewHabitName] = useState('');
  const [newHabitDesc, setNewHabitDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  useEffect(() => { fetchHabits(); }, []);

  const fetchHabits = async () => {
    try {
      const res = await axios.get(`${API}/habits/active`);
      setActiveHabits(res.data || []);
    } catch (error) {
      console.error('Failed to fetch habits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHabit = async (name = newHabitName, desc = newHabitDesc) => {
    if (!name.trim()) { toast.error('Please enter a habit name'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/habits/create`, {
        name: name.trim(),
        description: desc.trim() || null,
        target_days: 21
      });
      setShowCreate(false);
      setNewHabitName('');
      setNewHabitDesc('');
      toast.success('Habit added!');
      fetchHabits();
    } catch (error) {
      toast.error('Failed to create habit');
    } finally {
      setCreating(false);
    }
  };

  const handleConfirm = async (habitId) => {
    setConfirmingId(habitId);
    try {
      const res = await axios.post(`${API}/habits/confirm`, { habit_id: habitId });
      toast.success(`${res.data.current_streak} day streak!`);
      fetchHabits();
    } catch (error) {
      toast.error('Failed to confirm habit');
    } finally {
      setConfirmingId(null);
    }
  };

  const handleDelete = async (habitId) => {
    try {
      await axios.delete(`${API}/habits/${habitId}`);
      toast.success('Habit removed');
      fetchHabits();
    } catch (error) {
      toast.error('Failed to remove habit');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  // Filter suggested habits that aren't already active
  const activeNames = new Set(activeHabits.map(h => h.name));
  const availableSuggestions = suggestedHabits.filter(s => !activeNames.has(s.name));

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading habits...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-nav">
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h1 className="font-heading font-bold text-lg">Habit Stacker</h1>
            <p className="text-xs text-muted-foreground">
              {activeHabits.length} active habit{activeHabits.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => setShowCreate(true)}
              className="rounded-full gap-1"
              data-testid="add-habit-btn"
            >
              <Plus size={16} /> Add
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* Active Habits */}
        {activeHabits.length > 0 ? (
          <section>
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
              Active Habits
            </h2>
            <div className="space-y-3">
              {activeHabits.map((habit, idx) => {
                const isCompletedToday = habit.completed_days?.includes(today);
                const progress = (habit.current_streak / habit.target_days) * 100;

                return (
                  <motion.div
                    key={habit.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card className="glass-card p-4" data-testid={`habit-card-${idx}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-xl ${isCompletedToday ? 'bg-[#8BB89F]/10' : 'bg-primary/10'}`}>
                            <Target size={18} className={isCompletedToday ? 'text-[#8BB89F]' : 'text-primary'} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-sm">{habit.name}</h3>
                            {habit.description && (
                              <p className="text-xs text-muted-foreground">{habit.description}</p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDelete(habit.id)}
                          className="p-1 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                          data-testid={`delete-habit-${idx}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      {/* Streak + Progress */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex items-center gap-1.5">
                          {habit.current_streak >= 3 && <Flame size={14} className="text-[#D54B48]" />}
                          <span className="text-xs font-medium">
                            {habit.current_streak} / {habit.target_days} days
                          </span>
                        </div>
                        <div className="flex-1">
                          <Progress value={progress} className="h-1.5" />
                        </div>
                      </div>

                      {/* 21-day progress chain */}
                      <div className="mb-3">
                        <p className="text-xs text-muted-foreground mb-2">21-Day Chain</p>
                        <div className="flex flex-wrap gap-1">
                          {Array.from({ length: 21 }).map((_, i) => {
                            const startDate = new Date(habit.created_at || Date.now());
                            const targetDate = new Date(startDate);
                            targetDate.setDate(startDate.getDate() + i);
                            const dateStr = targetDate.toISOString().split('T')[0];
                            const done = habit.completed_days?.includes(dateStr);
                            const isToday = dateStr === today;
                            const isPast = targetDate < new Date(today);
                            const isFuture = targetDate > new Date(today);
                            
                            return (
                              <div
                                key={i}
                                title={`Day ${i + 1}: ${dateStr}`}
                                className={`w-4 h-4 rounded-sm flex items-center justify-center text-[8px] font-medium transition-all ${
                                  done
                                    ? 'bg-[#8BB89F] text-white'
                                    : isToday
                                    ? 'bg-primary/30 text-primary border border-primary ring-2 ring-primary/20'
                                    : isPast
                                    ? 'bg-muted text-muted-foreground'
                                    : 'bg-muted/30 text-muted-foreground/50'
                                }`}
                              >
                                {done ? '✓' : i + 1}
                              </div>
                            );
                          })}
                        </div>
                        {habit.current_streak >= 21 && (
                          <div className="mt-2 p-2 rounded-lg bg-[#8BB89F]/10 text-[#8BB89F] text-xs font-medium text-center">
                            Habit Established! Keep the momentum going.
                          </div>
                        )}
                      </div>

                      {/* Confirm button */}
                      <Button
                        onClick={() => handleConfirm(habit.id)}
                        disabled={confirmingId === habit.id || isCompletedToday}
                        className={`w-full rounded-full h-10 ${
                          isCompletedToday ? 'bg-[#8BB89F] hover:bg-[#8BB89F]' : 'bg-primary hover:bg-primary/90'
                        }`}
                        data-testid={`confirm-habit-${idx}`}
                      >
                        {isCompletedToday ? (
                          <><Check size={16} className="mr-1" /> Done Today</>
                        ) : confirmingId === habit.id ? 'Saving...' : (
                          <><Flame size={16} className="mr-1" /> Mark Complete</>
                        )}
                      </Button>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </section>
        ) : (
          <Card className="glass-card p-8 text-center">
            <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
              <Target size={32} className="text-primary" />
            </div>
            <h3 className="font-heading font-bold text-lg mb-2">No Active Habits</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Pick from suggestions below or create your own.
            </p>
          </Card>
        )}

        {/* Suggested Habits */}
        {availableSuggestions.length > 0 && (
          <section>
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
              Suggested Habits
            </h2>
            <div className="space-y-2">
              {availableSuggestions.map((s, i) => (
                <motion.div
                  key={s.name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card
                    className="glass-card p-3 cursor-pointer hover:border-primary/30 transition-colors"
                    onClick={() => handleCreateHabit(s.name, s.description)}
                    data-testid={`suggested-habit-${i}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{s.name}</p>
                        <p className="text-xs text-muted-foreground">{s.description}</p>
                      </div>
                      <Plus size={18} className="text-primary shrink-0" />
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Create Custom Habit Modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-sm"
            >
              <Card className="glass-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-bold text-xl">New Habit</h2>
                  <button onClick={() => setShowCreate(false)} className="p-1 rounded-full hover:bg-muted">
                    <X size={18} />
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="habit-name">Habit Name</Label>
                    <Input
                      id="habit-name"
                      placeholder="e.g., Morning Meditation"
                      value={newHabitName}
                      onChange={(e) => setNewHabitName(e.target.value)}
                      className="rounded-xl h-12"
                      data-testid="habit-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="habit-desc">Description (Optional)</Label>
                    <Input
                      id="habit-desc"
                      placeholder="e.g., 10 minutes every morning"
                      value={newHabitDesc}
                      onChange={(e) => setNewHabitDesc(e.target.value)}
                      className="rounded-xl h-12"
                      data-testid="habit-desc-input"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1 rounded-full">
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleCreateHabit()}
                    disabled={creating || !newHabitName.trim()}
                    className="flex-1 bg-primary hover:bg-primary/90 rounded-full"
                    data-testid="create-habit-btn"
                  >
                    {creating ? 'Creating...' : 'Add Habit'}
                  </Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
}
